import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/lib/i18n";
import { Loader2, Sparkles, ArrowUp, RotateCcw, X } from "lucide-react";

const SUGGESTED = {
  en: [
    "What did the team ship this week?",
    "Who's blocked right now?",
    "What's at risk before Friday?",
    "Who deserves a thank-you this week?",
  ],
  ar: [
    "ماذا أنجز الفريق هذا الأسبوع؟",
    "من هو المحظور الآن؟",
    "ما المعرّض للخطر قبل نهاية الأسبوع؟",
    "من يستحق الشكر هذا الأسبوع؟",
  ],
} as const;

type ChatTurn =
  | { role: "user"; content: string }
  | { role: "assistant"; content: string };

/**
 * Conversational team Q&A. The dialog now keeps a chat thread so
 * the user can ask follow-ups ("drill into Alice's blockers",
 * "what about Tuesday?") and the model resolves pronouns / time
 * references against the previous turn(s).
 *
 * Implementation notes:
 *   - `messages` holds the whole thread; we send the *prior* turns
 *     to the server as `history` so it can rebuild the prompt.
 *   - The data snapshot (members, signals, dates) is rebuilt
 *     server-side every call, so a follow-up about a fresher day
 *     still gets the latest signals — history only carries
 *     conversational continuity.
 *   - Suggested prompts only render in the empty state; once a
 *     conversation is going, they'd be noise.
 *   - Last 12 turns are passed to the server (server enforces the
 *     cap too); older turns drop off silently to keep prompts cheap.
 */
const HISTORY_TURN_CAP = 12;

export function AskTeamDialog({
  open,
  onOpenChange,
  weekStartKey,
  weekLabel,
  teamId,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  weekStartKey: string;
  weekLabel: string;
  /** When null, dialog explains the user has no team yet. */
  teamId: number | null;
}) {
  const { locale, t } = useI18n();
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState<ChatTurn[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const ask = trpc.team.ask.useMutation({
    onSuccess: r => {
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: r.answer },
      ]);
    },
    onError: () => {
      // Pop the last user message so the user can retry; the failed
      // turn shouldn't pollute history when it had no answer.
      setMessages(prev => {
        if (prev.length === 0 || prev[prev.length - 1].role !== "user") {
          return prev;
        }
        return prev.slice(0, -1);
      });
    },
  });

  // Reset state every time the dialog opens fresh
  useEffect(() => {
    if (open) {
      setQuestion("");
      setMessages([]);
      ask.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-scroll to bottom whenever a new message lands.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, ask.isPending]);

  function submit(q?: string) {
    const text = (q ?? question).trim();
    if (!text || !teamId || ask.isPending) return;
    // Append the user turn locally before firing the mutation so
    // the chat updates immediately even on slow networks.
    const newUserTurn: ChatTurn = { role: "user", content: text };
    const nextMessages: ChatTurn[] = [...messages, newUserTurn];
    setMessages(nextMessages);
    setQuestion("");
    // History sent to the server = everything BEFORE this new
    // question (the model sees the new question separately).
    const trimmedHistory = messages.slice(-HISTORY_TURN_CAP);
    ask.mutate({
      teamId,
      weekStartKey,
      weekLabel,
      locale,
      question: text,
      history: trimmedHistory,
    });
  }

  function newThread() {
    setMessages([]);
    setQuestion("");
    ask.reset();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Custom X (inline in the header) instead of the default
        // `float-end sticky` close button — the default fights with
        // the inline "New thread" action and lands in the wrong
        // place when the title row uses its own flex layout.
        showCloseButton={false}
        // Near-full-screen on tablet/desktop so a long chat thread
        // doesn't immediately collapse into a scrolling pane.
        // Mobile already takes the full viewport via the bottom-
        // sheet override below.
        className="
          sm:max-w-3xl sm:w-[min(95vw,56rem)] sm:max-h-[92dvh] sm:h-[92dvh]
          max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0
          max-sm:w-full max-sm:max-w-none max-sm:h-[100dvh] max-sm:max-h-[100dvh]
          max-sm:rounded-b-none max-sm:rounded-t-2xl
          max-sm:pt-[calc(1rem+env(safe-area-inset-top))] max-sm:pb-[calc(1rem+env(safe-area-inset-bottom))]
          flex flex-col overflow-hidden
        "
      >
        {/* Header: title on the left, secondary actions ("New thread"
            when a conversation is active) + X close on the right.
            Using a regular flex row instead of DialogHeader so we
            have full control over the action layout. The DialogTitle
            below stays for screen-reader/Radix semantics. */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <DialogTitle className="flex items-center gap-2 font-display text-lg">
              <Sparkles className="h-4 w-4 text-coral shrink-0" />
              <span className="truncate">
                {locale === "ar"
                  ? "اسأل عن أسبوع الفريق"
                  : "Ask about the team's week"}
              </span>
            </DialogTitle>
            <DialogDescription className="mt-0.5">
              {weekLabel}
            </DialogDescription>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {messages.length > 0 && (
              <button
                type="button"
                onClick={newThread}
                title={locale === "ar" ? "محادثة جديدة" : "New thread"}
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-muted transition-colors"
              >
                <RotateCcw className="h-3 w-3" />
                <span className="hidden sm:inline">
                  {locale === "ar" ? "محادثة جديدة" : "New thread"}
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label={locale === "ar" ? "إغلاق" : "Close"}
              title={locale === "ar" ? "إغلاق" : "Close"}
              className="h-8 w-8 grid place-items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {!teamId ? (
          <div className="py-6 text-sm text-muted-foreground">
            {locale === "ar"
              ? "لم تنضم بعد إلى أي فريق. تواصل مع المسؤول لإضافتك."
              : "You're not in a team yet. Once a teammate adds you, you can ask questions across everyone's week."}
          </div>
        ) : (
          <>
            {/* Chat thread. Scrolls within the dialog body so the
                composer + suggested chips stay pinned at the bottom. */}
            <div
              ref={scrollRef}
              className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1 py-2 space-y-3"
            >
              {messages.length === 0 && !ask.isPending && (
                <div className="text-sm text-muted-foreground py-4 text-center">
                  {locale === "ar"
                    ? "اسأل عن ما شُحن، ومن هو المحظور، وما هو المعرّض للخطر. الإجابات من إشارات حقيقية، لا تلفيق."
                    : "Ask what shipped, who's blocked, what's at risk. Answers come from real signals, never made up."}
                </div>
              )}
              {messages.map((m, i) => (
                <ChatMessage key={i} role={m.role} content={m.content} />
              ))}
              {ask.isPending && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground ms-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {locale === "ar"
                    ? "جاري البحث في إشارات الفريق…"
                    : "Reading the team's signals…"}
                </div>
              )}
              {ask.error && !ask.isPending && (
                <div className="flex items-start justify-between gap-3 text-red-700 text-sm rounded-lg border border-red-300/40 bg-red-50/60 dark:bg-red-900/20 p-3">
                  <div>
                    <div className="font-medium">{t.errorAskTeam}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {/* GENERIC ONLY. The server error message can carry the provider's own
                          text — `invokeLLM` throws `LLM invoke failed: <status> <statusText>
                          – <body>` — so rendering it would put provider prose, and anything
                          it quotes, in front of a team member. Operational detail belongs to
                          Admin Health, which is platform-admin only. */}
                      {locale === "ar"
                        ? "تعذّر إكمال الطلب الآن. حاول مرة أخرى."
                        : "That could not be completed right now. Please try again."}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      // Re-submit the last user question if it was
                      // popped from history when the error landed.
                      const last = messages[messages.length - 1];
                      if (last?.role === "user") {
                        submit(last.content);
                      }
                    }}
                    className="text-sm underline hover:no-underline shrink-0"
                  >
                    {t.retry}
                  </button>
                </div>
              )}
            </div>

            {/* Suggested chips — only visible at the start of a
                thread so they don't clutter mid-conversation. */}
            {messages.length === 0 && (
              <div className="flex flex-wrap gap-2 pb-2 shrink-0">
                {SUGGESTED[locale].map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => submit(s)}
                    className="text-xs rounded-full bg-muted hover:bg-coral-soft hover:text-coral px-3 py-1.5 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={e => {
                e.preventDefault();
                submit();
              }}
              className="flex items-center gap-2 shrink-0"
            >
              <Input
                placeholder={
                  messages.length > 0
                    ? locale === "ar"
                      ? "تابع السؤال…"
                      : "Follow up…"
                    : locale === "ar"
                      ? "اكتب سؤالك…"
                      : "Type a question…"
                }
                value={question}
                onChange={e => setQuestion(e.target.value)}
                autoFocus
                // When the field is focused and the keyboard slides up,
                // bring the latest message into view. The composer lifts
                // via --keyboard-height; fire across the keyboard
                // animation so the final scroll lands after the thread
                // has re-flowed to its smaller height.
                onFocus={() => {
                  requestAnimationFrame(scrollToBottom);
                  setTimeout(scrollToBottom, 200);
                  setTimeout(scrollToBottom, 400);
                  setTimeout(scrollToBottom, 650);
                }}
              />
              <Button
                type="submit"
                disabled={!question.trim() || ask.isPending}
                className="bg-coral hover:bg-coral/90 text-white"
                aria-label="Ask"
              >
                {ask.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowUp className="h-4 w-4" />
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChatMessage({
  role,
  content,
}: {
  role: "user" | "assistant";
  content: string;
}) {
  if (role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-tr-md bg-coral text-white px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words">
          {content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] rounded-2xl rounded-tl-md bg-muted/60 px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words">
        {content}
      </div>
    </div>
  );
}
